import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { StripeAccountStatus } from "@/modules/payment/stripeTypes"
import { ForbiddenIcon } from "@images/svgIcons/forbiddenIcon"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { word } from "@modules/core/utils/i18n"
import { useStripeAccount } from "@modules/finances/hooks/useStripeAccount"
import { getBoxPath } from "@modules/navigation/hooks/useBoxNavigate"
import { Path } from "@modules/navigation/routes"
import { useUser } from "@modules/users/hooks/useUser"
import { LoaderCircle } from "lucide-react"
import { useNavigate } from "react-router"
import styled from "styled-components"

export function StripeStatusBanner({ className, ...props }: React.ComponentProps<"div">) {
  const { result: box } = useCurrentBox()
  const { updateStripeAccount, loading: actionLoading } = useStripeAccount()
  const navigate = useNavigate()
  const user = useUser()

  return (
    <div
      className={cn(
        "relative flex w-full overflow-hidden rounded-md bg-purple-900 p-7 px-6 before:absolute before:top-0 before:left-0 before:h-full before:w-1 before:bg-purple-500 before:content-['']",
        className,
      )}
      {...props}
    >
      <div className="flex grow flex-row items-center justify-between">
        <div className="flew-row flex items-center gap-3">
          <IconWrapper>
            <ForbiddenIcon color="currentColor" className="text-textOnColor-body" />
          </IconWrapper>
          <div className="flex flex-col gap-0">
            <Text fontStyle="BL" className="text-textOnColor-body">
              {word("finances.page.banner.stripe.alert.title")}
            </Text>
            <Text fontStyle="BM" className="text-textOnColor-body">
              {word("finances.page.banner.stripe.alert.description")}
            </Text>
          </div>
        </div>

        <Button
          variant="primary"
          disabled={actionLoading}
          onClick={() => {
            if (box) navigate({ pathname: getBoxPath(Path.Box.Admin, { newBoxId: box.id }), search: "tab=banking" })
            else if (
              user.stripeAccountStatus === StripeAccountStatus.OK ||
              user.stripeAccountStatus === StripeAccountStatus.REQUIRE_ADDITIONAL_INFORMATION ||
              user.stripeAccountStatus === StripeAccountStatus.ONBOARDING
            )
              updateStripeAccount()
            else throw new Error("Stripe account is not created")
          }}
          isLoading={actionLoading}
        >
          {actionLoading && <LoaderCircle className="animate-spin" />}
          {word("finances.page.banner.stripe.alert.button.label")}
        </Button>
      </div>
    </div>
  )
}

const IconWrapper = styled.div`
  display: flex;
  border-radius: 50px;
  padding: 13px;
  background-color: var(--primary-800);
  justify-content: center;
  align-items: center;
`
